import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { Button, Divider, Icon } from '@kontentino/ui';
import Footer from 'app/modules/posts/components/shiftPosts/Footer';
import TargetProfileForm from 'app/modules/posts/components/shiftPosts/TargetProfileForm';
import { STEPS, useShiftPosts } from 'app/modules/posts/hooks/useShiftPosts';
import { useGetInitialTargetsToDuplicate } from 'app/modules/posts/modules/duplicatePosts/hooks/useGetInitialTargetsToDuplicate';
import { usePostsContainer } from 'components/posts/context';
import Modal from 'components/shared/Modal';
import StepsHeader from 'components/shared/workflowOptions/StepsHeader';
import PostsSelection from 'components/workflowOptions/PostsSelection';
import PostsSelectionContainer from 'components/workflowOptions/postsSelection/PostsSelectionContainer';
import { UserStore } from 'modules/user/userReducer';
import { useUser } from 'modules/user/userSelector';
import { useEffect } from 'react';
import { PageData, PagesProjects } from 'types/Page';
import { useEffectOnce } from 'utils/hooks/useEffectOnce';
import { useTranslation } from 'react-i18next';

type Props = {
  onFinish: () => void;
  pages: PageData[];
  user: UserStore;
  initialProjects?: PagesProjects;
  postsProjects?: PagesProjects;
};

const visibleSteps = Object.values(STEPS).slice(0, -1);

function DuplicatePostsPostsSelection(props: Props) {
  const { t } = useTranslation();
  const container = useShiftPosts(props);
  const { availableStatuses } = useUser();
  const { activeState } = usePostsContainer();
  const getInitialTargets = useGetInitialTargetsToDuplicate({
    initialPosts: container.selectedPosts,
    initialProjects: props.initialProjects,
    postsProjects: props.postsProjects,
  });

  useEffect(() => {
    const detectedInitialTargets = getInitialTargets();

    if (container.step === STEPS.SELECT_TARGET_PROFILES.step) {
      container.setTargets(detectedInitialTargets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container.step]);

  useEffectOnce(() => {
    container.addTarget();
  });

  container.targetHandlers.current = {};

  const allowedStatuses = availableStatuses(
    activeState.profiles.map((page) => page.type),
  );

  return (
    <>
      <Modal.Header
        title={t('duplicatePosts')}
        onGoBack={
          container.step === STEPS.SELECT_TARGET_PROFILES.step
            ? container.goBack
            : undefined
        }
      >
        {!container.isLastStep && (
          <StepsHeader steps={visibleSteps} activeStep={container.step} />
        )}
      </Modal.Header>
      <Divider />
      <Modal.Content className="tw-pt-4">
        {container.step === STEPS.SELECT_POSTS.step && (
          <PostsSelectionContainer
            allowedStatuses={allowedStatuses}
            selectedStatuses={container.selectedStatuses}
          >
            {(params) => (
              <PostsSelection
                allowedStatuses={allowedStatuses}
                selectedStatuses={container.selectedStatuses}
                onSelectPostStatus={container.togglePostStatus}
                posts={params.posts}
                onSelectPost={container.togglePost}
                selectedPosts={container.selectedPosts}
                onSelectAllPosts={() => container.selectPosts(params.posts)}
                onUnselectAllPosts={() => container.selectPosts([])}
              />
            )}
          </PostsSelectionContainer>
        )}

        {container.canShowTargetProfiles && (
          <>
            <div className="tw-mb-2 tw-flex tw-flex-col tw-gap-y-4">
              {container.canShowTargetProfiles &&
                container.targets.map((target, index) => (
                  <TargetProfileForm
                    selectedPosts={container.selectedPosts}
                    availablePages={props.pages}
                    dataNamePrefix="posts_duplicate-target"
                    initialTarget={target}
                    targetIndex={index + 1}
                    key={target.id}
                    removeTarget={
                      container.targets.length > 1
                        ? () => container.removeTarget(target.id)
                        : undefined
                    }
                    ref={(ref) => {
                      if (ref)
                        container.targetHandlers.current[target.id] = ref;
                    }}
                    error={container.errors[target.id]}
                  />
                ))}
            </div>
          </>
        )}
        {container.step === STEPS.SELECT_TARGET_PROFILES.step && (
          <Button
            variant="tertiary"
            onClick={container.addTarget}
            iconBefore={<Icon icon={faPlus} />}
          >
            {t('addProfile')}
          </Button>
        )}
      </Modal.Content>
      <Footer
        variant="duplicate"
        activeStep={container.step}
        canGoNext={container.canGoNext}
        onGoNext={container.goNext}
        onSubmit={container.onSubmit}
      />
    </>
  );
}

export default DuplicatePostsPostsSelection;
